import {create} from 'zustand';
import {subscribeWithSelector} from 'zustand/middleware';
import {getAllReferences} from '../yMaps/referencesYMap';

const DEBUG_REFERENCE_NUMBERS =
    typeof window !== 'undefined' &&
    (window.__DEBUG_REFERENCE_NUMBERS__ || localStorage.getItem('DEBUG_REFERENCE_NUMBERS') === '1');

/**
 * Calculate citation numbers for references based on document order.
 * Each unique localId gets a number based on its first appearance.
 *
 * @param {Array<string>} markOrder - Array of referenceMarkIds in document order
 * @param {Map}           referencesByMark - Map(referenceMarkId -> Array of reference objects)
 * @returns {Map} Map(localId -> citation number)
 */
function calculateCitationNumbers(markOrder, referencesByMark) {
    const numbersByLocalId = new Map();
    let nextNumber = 1;

    markOrder.forEach((markId) => {
        const references = referencesByMark.get(markId);
        if (!references) return;

        references.forEach((ref) => {
            const localId = ref.localId;
            if (!localId) return;
            if (!numbersByLocalId.has(localId)) {
                numbersByLocalId.set(localId, nextNumber);
                nextNumber++;
            }
        });
    });

    return numbersByLocalId;
}

/**
 * Zustand store for managing reference citation numbering.
 *
 * Data source: Y.Map("referenceMarks") observed via observeDeep.
 * The store is flat — one editor session, one Y.Doc, no per-reportId keying.
 *
 * Public methods that previously accepted a reportId still accept it for call-site
 * compatibility (the ProseMirror plugins pass it), but the reportId is ignored
 * internally — there is only one active document at a time.
 *
 * @example
 *   // In MarginRail (React):
 *   const subscribeToReferences = useReferenceNumberStore(s => s.subscribeToReferences);
 *   useEffect(() => subscribeToReferences(yReferenceMarks), [yReferenceMarks]);
 *
 *   // In ProseMirror plugin (non-React):
 *   const number = useReferenceNumberStore.getState().getReferenceNumber(reportId, localId);
 */
const useReferenceNumberStore = create(subscribeWithSelector((set, get) => ({
    // Map(localId -> citation number) — flat, not keyed by reportId
    numbersByLocalId: new Map(),

    // Array<string> of referenceMarkIds in document order
    referenceMarkOrder: [],

    // Map(referenceMarkId -> Array of reference objects) — from Y.Map
    referencesByMark: new Map(),

    // Monotonic counter — bumped on every citation-relevant change
    citationVersion: 0,

    // Y.Map unobserve handle — null when not subscribed
    _unsubscribe: null,


    /**
     * Subscribe to Y.Map("referenceMarks") for citation numbering.
     * Replaces the old Firestore onSnapshot subscription.
     *
     * Idempotent: calling again with a different yReferenceMarks first tears down
     * the previous subscription, then sets up a new one.
     *
     * @param {import('yjs').Map} yReferenceMarks
     * @returns {Function} Unsubscribe function
     */
    subscribeToReferences: (yReferenceMarks) => {
        if (!yReferenceMarks) return () => {
        };

        // Tear down existing subscription if any
        const existing = get()._unsubscribe;
        if (existing) existing();

        const rebuild = () => {
            const referencesByMark = getAllReferences(yReferenceMarks);
            const markOrder = get().referenceMarkOrder;

            const numbersByLocalId = markOrder.length > 0
                ? calculateCitationNumbers(markOrder, referencesByMark)
                : new Map();

            if (DEBUG_REFERENCE_NUMBERS) {
                console.log('[referenceNumbers] Y.Map observer fired', {
                    marks: referencesByMark.size,
                    numbers: numbersByLocalId.size,
                });
            }

            set((state) => ({
                referencesByMark,
                numbersByLocalId,
                citationVersion: state.citationVersion + 1,
            }));
        };

        // Read current state immediately
        rebuild();

        // Subscribe to all future changes
        yReferenceMarks.observeDeep(rebuild);

        const unsubscribe = () => {
            yReferenceMarks.unobserveDeep(rebuild);
            set({_unsubscribe: null});
        };

        set({_unsubscribe: unsubscribe});
        return unsubscribe;
    },


    /**
     * Tear down the Y.Map subscription and clear all citation data.
     * Call when the editor unmounts.
     */
    unsubscribeFromReferences: () => {
        const unsub = get()._unsubscribe;
        if (unsub) unsub();

        set({
            numbersByLocalId: new Map(),
            referenceMarkOrder: [],
            referencesByMark: new Map(),
            citationVersion: 0,
            _unsubscribe: null,
        });
    },


    /**
     * Update the document order of reference marks.
     * Called by the ProseMirror marginMarkersPlugin whenever marks move.
     * Recalculates citation numbers immediately.
     *
     * The `reportId` parameter is accepted for call-site compatibility but ignored.
     *
     * @param {string}                          _reportId   (unused — kept for compat)
     * @param {Array<{uid: string, from: number}>} orderedMarks
     */
    updateReferenceOrder: (_reportId, orderedMarks) => {
        if (!orderedMarks) return;

        try {
            const state = get();
            const newOrder = orderedMarks.map(m => m?.uid).filter(Boolean);
            const currentOrder = state.referenceMarkOrder;

            // Skip if order unchanged
            const orderChanged =
                currentOrder.length !== newOrder.length ||
                newOrder.some((uid, idx) => uid !== currentOrder[idx]);

            if (!orderChanged) return;

            const numbersByLocalId = newOrder.length > 0
                ? calculateCitationNumbers(newOrder, state.referencesByMark)
                : new Map();

            set((prev) => ({
                referenceMarkOrder: newOrder,
                numbersByLocalId,
                citationVersion: prev.citationVersion + 1,
            }));
        } catch (error) {
            console.error('Error in updateReferenceOrder:', error, {orderedMarks});
        }
    },


    /**
     * Get the citation number for a specific reference.
     * The `reportId` parameter is accepted for call-site compatibility but ignored.
     *
     * @param {string} _reportId  (unused — kept for compat)
     * @param {string} localId
     * @returns {number|null}
     */
    getReferenceNumber: (_reportId, localId) => {
        return get().numbersByLocalId?.get(localId) ?? null;
    },


    /**
     * Get all citation numbers.
     * The `reportId` parameter is accepted for call-site compatibility but ignored.
     *
     * @param {string} _reportId  (unused — kept for compat)
     * @returns {Map<string, number>}
     */
    getAllReferenceNumbers: (_reportId) => {
        return get().numbersByLocalId || new Map();
    },


    /**
     * Get reference data for a specific mark.
     * The `reportId` parameter is accepted for call-site compatibility but ignored.
     *
     * @param {string} _reportId  (unused — kept for compat)
     * @param {string} referenceMarkId
     * @returns {Array|null}
     */
    getReferenceData: (_reportId, referenceMarkId) => {
        const refs = get().referencesByMark?.get(referenceMarkId);
        return refs && refs.length > 0 ? refs : null;
    },


    /**
     * Clear all data (full reset — e.g. on editor unmount).
     */
    clear: () => {
        const unsub = get()._unsubscribe;
        if (unsub) unsub();

        set({
            numbersByLocalId: new Map(),
            referenceMarkOrder: [],
            referencesByMark: new Map(),
            citationVersion: 0,
            _unsubscribe: null,
        });
    },
})));

export default useReferenceNumberStore;

